import { CustomRequestOptions } from '@/interceptors/request'
import { useUserStore } from '@/store'
import Router, { RouterOptions } from '@/utils/router'

export const http = <T>(options: CustomRequestOptions) => {
  // 1. 返回 Promise 对象
  return new Promise<T>((resolve, reject) => {
    uni.request({
      ...options,
      dataType: 'json',
      // #ifndef MP-WEIXIN
      responseType: 'json',
      // #endif
      async success(res) {
        if (res.statusCode >= 200 && res.statusCode < 300) {
          resolve(res.data as T)
        } else if (res.statusCode === 401) {
          // 检查是否是重试请求
          if (options._isRetry) {
            const useStore = useUserStore()
            useStore.clearUserInfo()
            !options.hideErrorToast && uni.showToast({ icon: 'none', title: '登录已过期' })
            reject(res)
            return
          }

          const useStore = useUserStore()
          const refreshToken = useStore.refreshToken

          if (!refreshToken) {
            useStore.clearUserInfo()
            reject(res)
            return
          }

          try {
            // 刷新 Token
            // eslint-disable-next-line promise/param-names
            const reloadRes = await new Promise<any>((innerResolve, innerReject) => {
              uni.request({
                url: '/oauth/token',
                method: 'POST',
                data: {
                  scope: 'write',
                  grant_type: 'refresh_token',
                  client_secret: 'secretkey',
                  client_id: 'app',
                  refresh_token: refreshToken,
                },
                header: {
                  'content-type': 'application/x-www-form-urlencoded',
                },
                dataType: 'json',
                success: innerResolve,
                fail: innerReject,
              })
            })

            if (reloadRes.statusCode === 200) {
              // 更新 Token 信息
              useStore.setTokenInfo({
                access_token: reloadRes.data.access_token,
                refresh_token: reloadRes.data.refresh_token,
              })

              // 重新发起原请求（携带新 Token）
              const newOptions: CustomRequestOptions = {
                ...options,
                _isRetry: true, // 标记为已重试
                header: {
                  ...options.header,
                  Authorization: `Bearer ${reloadRes.data.access_token}`,
                },
              }

              const retryResult = await http<T>(newOptions)
              resolve(retryResult)
            } else {
              useStore.clearUserInfo()
              RouterOptions.options = {
                url: UseRouter.PAGE_LOGIN_URL,
              }
              Router.reLaunch()
              reject(reloadRes)
            }
          } catch (error) {
            useStore.clearUserInfo()
            RouterOptions.options = {
              url: UseRouter.PAGE_LOGIN_URL,
            }
            Router.reLaunch()
            reject(error)
          }
        } else {
          !options.hideErrorToast &&
            uni.showToast({
              icon: 'none',
              title: (res.data as any).msg || '请求错误',
            })
          reject(res)
        }
      },
      fail(err) {
        uni.showToast({
          icon: 'none',
          title: '网络错误，请检查网络',
        })
        reject(err)
      },
    })
  })
}

/**
 * GET 请求
 * @param url 后台地址
 * @param query 请求query参数
 * @param header 请求头
 * @returns
 */
export const httpGet = <T>(
  url: string,
  query?: Record<string, any>,
  header?: Record<string, any>,
) => {
  return http<T>({
    url,
    query,
    header,
    method: 'GET',
  })
}

/**
 * POST 请求
 * @param url 后台地址
 * @param data 请求body参数
 * @param query 请求query参数，post请求也支持query，很多微信接口都需要
 * @param header
 * @returns
 */
export const httpPost = <T>(
  url: string,
  data?: Record<string, any>,
  query?: Record<string, any>,
  header?: Record<string, any>,
) => {
  return http<T>({
    url,
    data,
    query,
    header,
    method: 'POST',
  })
}

/**
 * PUT请求
 * @param url 后台地址
 * @param data 请求body参数
 * @param query 请求query参数，post请求也支持query，很多微信接口都需要
 * @param header
 * @returns
 */
export const httpPut = <T>(
  url: string,
  data?: Record<string, any>,
  query?: Record<string, any>,
  header?: Record<string, any>,
) => {
  return http<T>({
    url,
    data,
    query,
    header,
    method: 'PUT',
  })
}


/**
 * DELETE请求
 * @param url 后台地址
 * @param data 请求body参数
 * @param query 请求query参数，post请求也支持query，很多微信接口都需要
 * @param header
 * @returns
 */
export const httpDelete = <T>(
  url: string,
  data?: Record<string, any>,
  query?: Record<string, any>,
  header?: Record<string, any>,
) => {
  return http<T>({
    url,
    data,
    query,
    header,
    method: 'DELETE',
  })
}

http.get = httpGet
http.post = httpPost
http.put = httpPut
http.delete = httpDelete
